import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataElementView, DataSource, SourceFieldMappingView } from "@/entities/all";
// import { DataQualityMetricsView } from "@/entities/all"; // TODO: enable when data is available
import { Blocks, GitBranch, BookOpen, PlusCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { createPageUrl } from "@/utils";
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"; // TODO: enable with QualityTrendsChart

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function DonutChart({ segments }) {
  const size = 86;
  const sw = 18;
  const cx = size / 2;
  const cy = size / 2;
  const r = cx - sw / 2;
  let startAngle = 0;

  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--table-stripe)" strokeWidth={sw} />
      {segments.map((seg) => {
        if (seg.pct <= 0) return null;
        const span = seg.pct * 360;
        const endAngle = startAngle + span;
        const s = polarToCartesian(cx, cy, r, startAngle);
        const e = polarToCartesian(cx, cy, r, endAngle >= 360 ? 359.99 : endAngle);
        const largeArc = span > 180 ? 1 : 0;
        const d = `M ${s.x.toFixed(3)} ${s.y.toFixed(3)} A ${r} ${r} 0 ${largeArc} 1 ${e.x.toFixed(3)} ${e.y.toFixed(3)}`;
        startAngle = endAngle;
        return (
          <path key={seg.label} d={d} fill="none" stroke={seg.color} strokeWidth={sw} strokeLinecap="butt" />
        );
      })}
    </svg>
  );
}

const DONUT_COLORS = [
  { key: 'APPROVED',       color: 'var(--chart-blue)',   label: 'Approved'       },
  { key: 'PENDING_REVIEW', color: 'var(--chart-purple)', label: 'Pending Review' },
  { key: 'DRAFT',          color: 'var(--chart-amber)',  label: 'Draft'          },
  { key: 'DEPRECATED',     color: 'var(--chart-indigo)', label: 'Deprecated'     },
];

const STATUS_CONFIG = {
  APPROVED:       { bg: 'var(--pill-green-bg)',  color: 'var(--pill-green-color)',  label: 'Approved'       },
  PENDING_REVIEW: { bg: 'var(--pill-amber-bg)',  color: 'var(--pill-amber-color)',  label: 'Pending Review' },
  DRAFT:          { bg: 'var(--pill-blue-bg)',   color: 'var(--pill-blue-color)',   label: 'Draft'          },
  DEPRECATED:     { bg: 'var(--pill-red-bg)',    color: 'var(--pill-red-color)',    label: 'Deprecated'     },
};

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status?.toUpperCase()] || STATUS_CONFIG.DRAFT;
  return (
    <span
      className="inline-flex items-center gap-1 h-[18px] rounded-[9px] whitespace-nowrap text-xs font-medium"
      style={{ background: cfg.bg, color: cfg.color, padding: '2px 8px 2px 3px' }}
    >
      <span className="size-[10px] rounded-full shrink-0" style={{ background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

const COL = {
  name:   'flex-1 min-w-0',
  owner:  'w-[160px] shrink-0',
  date:   'w-[130px] shrink-0',
  status: 'w-[140px] shrink-0',
};

/* TODO: enable QualityTrendsChart when data is available
const TREND_LINES = [
  { key: 'accuracy',     color: 'var(--chart-amber)',  label: 'Accuracy'     },
  { key: 'completeness', color: 'var(--chart-purple)', label: 'Completeness' },
  { key: 'timeliness',   color: 'var(--chart-blue)',   label: 'Timeliness'   },
];

function QualityTrendsChart({ data }) { ... }
*/

export default function Dashboard() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [recentElements, setRecentElements] = useState([]);
  const [data, setData] = useState({
    elements: { total: 0, approved: 0 },
    sources:  { total: 0, active: 0 },
    quality:  0,
    mappings: 0,
    donut: DONUT_COLORS.map(d => ({ ...d, pct: 0, value: 0 })),
  });

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const [approvedR, pendingR, deprecatedR, totalR, recentR, sources, mappingsView] = await Promise.all([
        DataElementView.filterWithTotal({ status: 'APPROVED' },       '', 1, 0),
        DataElementView.filterWithTotal({ status: 'PENDING_REVIEW' }, '', 1, 0),
        DataElementView.filterWithTotal({ status: 'DEPRECATED' },     '', 1, 0),
        DataElementView.listWithTotal('', 1, 0),
        DataElementView.listWithTotal('updated_date', 50, 0),
        DataSource.list(),
        SourceFieldMappingView.list(),
        // DataQualityMetricsView.list('calculation_date', 1000, 0), // TODO: enable when data is available
      ]);

      const approved   = approvedR.total  || 0;
      const pendingRev = pendingR.total    || 0;
      const deprecated = deprecatedR.total || 0;
      const total      = totalR.total      || 0;
      const draft      = Math.max(0, total - approved - pendingRev - deprecated);
      const donutTotal = total || 1;

      // Recent elements: sort desc by updated_date, take 10
      const sorted = [...(recentR.data || [])]
        .sort((a, b) => new Date(b.updated_date) - new Date(a.updated_date))
        .slice(0, 10);
      setRecentElements(sorted);

      const activeSources = sources.filter(s => s.status === 'Active').length;
      const mappedFields  = mappingsView.filter(m => m.canonical_element_id).length;
      const totalFields   = mappingsView.length;

      const pct = (a, b) => b === 0 ? 0 : Math.round((a / b) * 100);
      const quality = Math.round((pct(approved, total) + pct(mappedFields, totalFields)) / 2);

      setData({
        elements: { total, approved },
        sources:  { total: sources.length, active: activeSources },
        quality,
        mappings: mappedFields,
        donut: [
          { ...DONUT_COLORS[0], pct: approved   / donutTotal, value: approved   },
          { ...DONUT_COLORS[1], pct: pendingRev / donutTotal, value: pendingRev },
          { ...DONUT_COLORS[2], pct: draft      / donutTotal, value: draft      },
          { ...DONUT_COLORS[3], pct: deprecated / donutTotal, value: deprecated },
        ],
      });
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const STAT_CARDS = [
    {
      to: '/data-elements',
      icon: <Blocks size={24} color="var(--chart-blue)" />,
      label: 'Total Elements',
      value: isLoading ? null : data.elements.total.toLocaleString(),
      description: isLoading ? null : `${data.elements.approved.toLocaleString()} approved`,
    },
    {
      to: '/data-sources',
      icon: <GitBranch size={24} color="var(--chart-purple)" />,
      label: 'Data Sources',
      value: isLoading ? null : data.sources.total.toLocaleString(),
      description: isLoading ? null : `${data.sources.active} active`,
    },
    {
      to: '/dictionary-quality',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M16 8V5L19 2L20 4L22 5L19 8H16ZM16 8L12 11.9999M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2M17 12C17 14.7614 14.7614 17 12 17C9.23858 17 7 14.7614 7 12C7 9.23858 9.23858 7 12 7" stroke="var(--chart-purple)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      label: 'Quality Score',
      value: isLoading ? null : `${data.quality}%`,
      description: 'Average across all elements',
    },
    {
      to: '/source-mappings',
      icon: <BookOpen size={24} color="var(--pill-green-strong-color)" />,
      label: 'Field Mappings',
      value: isLoading ? null : data.mappings.toLocaleString(),
      description: 'Source to canonical mappings',
    },
  ];

  return (
    <div data-tour="page-dashboard" className="pt-5 pb-24 px-4 md:py-[30px] md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-x-[20px] md:gap-y-3">

        {/* Title + System Healthy — always stay together */}
        <div className="flex items-center justify-between gap-3 md:gap-[20px]">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Dashboard</h1>
          <div
            className="flex items-center bg-card-bg shrink-0"
            style={{ height: 36, padding: '6px 16px 6px 12px', gap: 6, borderRadius: 18 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="var(--pill-green-strong-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-sm font-medium whitespace-nowrap" style={{ color: 'var(--pill-green-strong-color)' }}>System Healthy</span>
          </div>
        </div>

        {/* Search + Add Element — atomic group (wraps together; inside group they can also wrap each to own row if very tight) */}
        <div className="flex flex-wrap items-center gap-3 md:flex-auto md:justify-end">

          {/* Search */}
          <div className="flex items-center h-9 bg-card-bg rounded-[18px] px-3 gap-2 flex-1 min-w-[240px] md:max-w-[426px] overflow-hidden">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
              <path d="M21 21L17.5001 17.5M20 11.5C20 16.1944 16.1944 20 11.5 20C6.80558 20 3 16.1944 3 11.5C3 6.80558 6.80558 3 11.5 3C16.1944 3 20 6.80558 20 11.5Z" stroke="var(--nav-active)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              placeholder="Search elements..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent border-0 outline-none text-sm text-page-text placeholder:text-page-muted min-w-0"
              style={{ textOverflow: 'ellipsis' }}
            />
          </div>

          {/* Add Element */}
          <button className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors shrink-0">
            <PlusCircle size={24} />
            Add Element
          </button>
        </div>
      </div>

      {/* Stats + Chart row */}
      <div className="grid gap-[10px] grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(200px,1fr)_minmax(200px,1fr)_254px]">

        {STAT_CARDS.map((card) => (
          <Link
            key={card.label}
            to={card.to}
            className="bg-card-bg rounded-[8px] flex items-start gap-3 no-underline relative z-0 hover:z-10 transition-shadow hover:shadow-[0_14px_42px_0_var(--shadow-blue-lg)]"
            style={{ padding: '12px 20px', height: 108 }}
          >
            <div className="shrink-0 mt-0.5">{card.icon}</div>
            <div className="flex flex-col min-w-0" style={{ gap: 4 }}>
              <span className="text-sm font-medium text-page-text">{card.label}</span>
              {isLoading
                ? <Skeleton className="h-[30px] w-16 mt-1" />
                : <span className="text-[30px] font-medium text-page-text" style={{ lineHeight: 'normal' }}>{card.value}</span>
              }
              <span className="text-[11px] font-medium text-page-muted">{card.description}</span>
            </div>
          </Link>
        ))}

        {/* Donut chart block */}
        <div
          className="bg-card-bg rounded-[8px] flex items-start gap-3 sm:col-span-2 lg:col-span-1 lg:col-start-3 lg:row-start-1 lg:row-span-2"
          style={{ padding: '12px 20px' }}
        >
          <div className="shrink-0 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C13.3132 2 14.6136 2.25866 15.8268 2.76121C17.0401 3.26375 18.1425 4.00035 19.0711 4.92893C19.9997 5.85752 20.7363 6.95991 21.2388 8.17317C21.7413 9.38643 22 10.6868 22 12M12 2V12M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5229 22 12M12 2C17.5228 2 22 6.47716 22 12M22 12L12 12M22 12C22 13.5781 21.6265 15.1338 20.9101 16.5399C20.1936 17.946 19.1546 19.1626 17.8779 20.0902L12 12" stroke="var(--divider)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <div className="flex flex-col flex-1 min-w-0" style={{ gap: 8 }}>
            <span className="text-sm font-medium text-page-text">Elements by Status</span>
            {isLoading
              ? <Skeleton className="rounded-full" style={{ width: 86, height: 86 }} />
              : <DonutChart segments={data.donut} />
            }
            <div className="flex flex-col" style={{ gap: 4 }}>
              {data.donut.map((seg) => (
                <div key={seg.label} className="flex items-center gap-1">
                  <div className="shrink-0 rounded-full" style={{ width: 8, height: 8, backgroundColor: seg.color }} />
                  <span className="flex-1 truncate text-[11px] font-medium text-page-text">{seg.label}</span>
                  <span className="text-right shrink-0 text-[11px] font-medium text-page-text" style={{ width: 28 }}>
                    {isLoading ? '—' : seg.value}
                  </span>
                  <span className="text-right shrink-0 text-[11px] font-medium text-nav-active" style={{ width: 34 }}>
                    {isLoading ? '' : `(${Math.round(seg.pct * 100)}%)`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* TODO: Charts row — enable when data is available
      <div className="flex gap-[10px]">
        <div className="flex-1">
          <QualityTrendsChart data={[]} />
        </div>
        <div className="flex-1 bg-card-bg rounded-[8px]" style={{ height: 189 }} />
      </div>
      */}

      {/* Recent Activity */}
      <div className="flex flex-col gap-3">
        <span className="text-2xl font-medium text-nav-active">Recent Activity</span>

        <div className="bg-card-bg rounded-[8px] overflow-x-auto">
          <div className="flex flex-col min-w-[700px]">
            {/* Table header */}
            <div className="flex items-center h-9 px-5 gap-[10px] bg-table-header">
              <div className={`${COL.name} text-sm font-medium text-nav-active`}>Element name</div>
              <div className={`${COL.owner} text-sm font-medium text-nav-active`}>Owner</div>
              <div className={`${COL.date} text-sm font-medium text-nav-active`}>Last updated</div>
              <div className={`${COL.status} text-sm font-medium text-nav-active`}>Status</div>
            </div>

            {/* Rows */}
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={`flex items-center h-11 px-5 gap-[10px] ${i % 2 === 0 ? 'bg-table-stripe' : 'bg-card-bg'}`}>
                  <Skeleton className="h-4 w-48" />
                </div>
              ))
            ) : recentElements.length === 0 ? (
              <div className="flex items-center justify-center py-10">
                <span className="text-sm text-page-muted">No elements found</span>
              </div>
            ) : (
              recentElements.map((el, i) => {
                const status = el.status || 'DRAFT';
                return (
                  <div
                    key={el.id}
                    className={`group flex items-center h-11 px-5 gap-[10px] cursor-pointer ${i % 2 === 0 ? 'bg-table-stripe' : 'bg-card-bg'}`}
                    onClick={() => navigate(createPageUrl(`element-details?id=${el.id}`))}
                  >
                    <div className={`${COL.name} min-w-0`}>
                      <span className="truncate block text-sm font-normal text-page-text group-hover:text-nav-active transition-colors">
                        {el.canonical_name}
                      </span>
                    </div>
                    <div className={COL.owner}>
                      <span className={`truncate block text-sm font-normal ${el.owner ? 'text-nav-active' : 'text-page-muted'}`}>
                        {el.owner || '—'}
                      </span>
                    </div>
                    <div className={COL.date}>
                      <span className="text-sm font-normal text-nav-active">
                        {el.updated_date
                          ? format(new Date(el.updated_date), 'MMM d, yyyy')
                          : <span className="text-page-muted">—</span>}
                      </span>
                    </div>
                    <div className={COL.status}>
                      <StatusPill status={status} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
